import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { financingAPI } from '../../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

const INSTITUTION_TYPES = ['banque', 'microfinance', 'fonds_garantie', 'investisseur', 'ong', 'etat', 'autre'];
const FINANCING_TYPES = ['credit', 'subvention', 'investissement', 'garantie', 'autre'];
const MATURITY_LEVELS = ['idee', 'prototype', 'creation', 'amorcage', 'croissance', 'developpement', 'expansion'];

const INSTITUTION_LABELS = {
  banque: 'Banque',
  microfinance: 'Institution de microfinance',
  fonds_garantie: 'Fonds de garantie',
  investisseur: 'Investisseur / Capital-risque',
  ong: 'ONG',
  etat: 'État / Programme public',
  autre: 'Autre',
};

const FINANCING_TYPE_LABELS = {
  credit: 'Crédit / Prêt',
  subvention: 'Subvention / Don',
  investissement: 'Investissement en capital',
  garantie: 'Garantie',
  autre: 'Autre',
};

const MATURITY_LABELS = {
  idee: 'Idée',
  prototype: 'Prototype',
  creation: 'Création',
  amorcage: 'Amorçage',
  croissance: 'Croissance',
  developpement: 'Développement',
  expansion: 'Expansion / International',
};

const emptyForm = {
  name: '',
  institution_name: '',
  institution_type: 'banque',
  country_code: '',
  financing_type: 'credit',
  maturity_levels: [],
  sectors: '',
  description: '',
  amount_min: '',
  amount_max: '',
  currency: 'XOF',
  duration_months: '',
  interest_rate: '',
  eligibility: '',
  application_url: '',
  contact_email: '',
  contact_phone: '',
};

const FinancingManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const queryClient = useQueryClient();

  const { data: products = [], isLoading } = useQuery('admin-financing-products', async () => {
    const res = await financingAPI.adminListProducts({ limit: 100 });
    return Array.isArray(res.data) ? res.data : [];
  });

  const { data: meta } = useQuery('financing-meta', async () => {
    const res = await financingAPI.getMeta();
    return res.data;
  });

  const invalidate = () => queryClient.invalidateQueries('admin-financing-products');

  const createMutation = useMutation(
    (payload) => financingAPI.adminCreateProduct(payload),
    { onSuccess: () => { invalidate(); toast.success('Produit créé'); closeModal(); }, onError: () => toast.error('Erreur lors de la création') }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => financingAPI.adminUpdateProduct(id, payload),
    { onSuccess: () => { invalidate(); toast.success('Produit mis à jour'); closeModal(); }, onError: () => toast.error('Erreur lors de la mise à jour') }
  );

  const deleteMutation = useMutation(
    (id) => financingAPI.adminDeleteProduct(id),
    { onSuccess: () => { invalidate(); toast.success('Produit supprimé'); }, onError: () => toast.error('Erreur lors de la suppression') }
  );

  const togglePublishMutation = useMutation(
    ({ id, status }) => financingAPI.adminUpdateProduct(id, { status }),
    { onSuccess: () => { invalidate(); toast.success('Statut mis à jour'); }, onError: () => toast.error('Erreur lors du changement de statut') }
  );

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingId(product.id);
    setForm({
      name: product.name || '',
      institution_name: product.institution_name || '',
      institution_type: product.institution_type || 'banque',
      country_code: product.country_code || '',
      financing_type: product.financing_type || 'credit',
      maturity_levels: product.maturity_levels || [],
      sectors: (product.sectors || []).join(', '),
      description: product.description || '',
      amount_min: product.amount_min ?? '',
      amount_max: product.amount_max ?? '',
      currency: product.currency || 'XOF',
      duration_months: product.duration_months ?? '',
      interest_rate: product.interest_rate || '',
      eligibility: product.eligibility || '',
      application_url: product.application_url || '',
      contact_email: product.contact_email || '',
      contact_phone: product.contact_phone || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const toggleMaturity = (level) => {
    setForm((prev) => ({
      ...prev,
      maturity_levels: prev.maturity_levels.includes(level)
        ? prev.maturity_levels.filter((m) => m !== level)
        : [...prev.maturity_levels, level],
    }));
  };

  const handleSubmit = (e, status) => {
    e.preventDefault();

    const payload = {
      ...form,
      country_code: form.country_code.toUpperCase(),
      sectors: form.sectors.split(',').map((s) => s.trim()).filter(Boolean),
      amount_min: form.amount_min === '' ? null : Number(form.amount_min),
      amount_max: form.amount_max === '' ? null : Number(form.amount_max),
      duration_months: form.duration_months === '' ? null : parseInt(form.duration_months, 10),
      status,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-text">Catalogue de financement</h2>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Nouveau produit
        </button>
      </div>

      {isLoading ? (
        <p className="text-gray-600">Chargement...</p>
      ) : products.length === 0 ? (
        <p className="text-gray-600">Aucun produit de financement pour le moment.</p>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Institution</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pays</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id}>
                  <td className="px-4 py-3 text-sm font-medium text-text">{product.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{product.institution_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{FINANCING_TYPE_LABELS[product.financing_type] || product.financing_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{product.country_code}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${product.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {product.status === 'published' ? 'Publié' : 'Brouillon'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => togglePublishMutation.mutate({ id: product.id, status: product.status === 'published' ? 'draft' : 'published' })}
                      title={product.status === 'published' ? 'Dépublier' : 'Publier'}
                      className="text-gray-500 hover:text-primary-600"
                    >
                      {product.status === 'published' ? <EyeSlashIcon className="w-5 h-5 inline" /> : <EyeIcon className="w-5 h-5 inline" />}
                    </button>
                    <button onClick={() => openEditModal(product)} title="Modifier" className="text-gray-500 hover:text-primary-600">
                      <PencilIcon className="w-5 h-5 inline" />
                    </button>
                    <button
                      onClick={() => { if (window.confirm('Supprimer ce produit de financement ?')) deleteMutation.mutate(product.id); }}
                      title="Supprimer"
                      className="text-gray-500 hover:text-red-600"
                    >
                      <TrashIcon className="w-5 h-5 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-semibold text-text mb-4">
              {editingId ? 'Modifier le produit de financement' : 'Nouveau produit de financement'}
            </h3>

            <form onSubmit={(e) => handleSubmit(e, form.status || 'draft')} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du produit</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
                  <input required value={form.institution_name} onChange={(e) => setForm({ ...form, institution_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type d'institution</label>
                  <select value={form.institution_type} onChange={(e) => setForm({ ...form, institution_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {INSTITUTION_TYPES.map((t) => <option key={t} value={t}>{INSTITUTION_LABELS[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de financement</label>
                  <select value={form.financing_type} onChange={(e) => setForm({ ...form, financing_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    {FINANCING_TYPES.map((t) => <option key={t} value={t}>{FINANCING_TYPE_LABELS[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code pays (ISO 2 lettres)</label>
                  <select required value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="">Sélectionner...</option>
                    {(meta?.countries || []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Devise</label>
                  <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant minimum</label>
                  <input type="number" min="0" value={form.amount_min} onChange={(e) => setForm({ ...form, amount_min: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Montant maximum</label>
                  <input type="number" min="0" value={form.amount_max} onChange={(e) => setForm({ ...form, amount_max: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durée (mois)</label>
                  <input type="number" min="0" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taux d'intérêt</label>
                  <input value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                    placeholder="ex: 8% par an" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Niveaux de maturité ciblés</label>
                <div className="flex flex-wrap gap-2">
                  {MATURITY_LEVELS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => toggleMaturity(m)}
                      className={`px-3 py-1 rounded-full text-sm border ${form.maturity_levels.includes(m) ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-700'}`}
                    >
                      {MATURITY_LABELS[m]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secteurs (séparés par des virgules)</label>
                <input value={form.sectors} onChange={(e) => setForm({ ...form, sectors: e.target.value })}
                  placeholder="ex: Agriculture, Numérique, Santé" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Critères d'éligibilité</label>
                <textarea rows={2} value={form.eligibility} onChange={(e) => setForm({ ...form, eligibility: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lien de demande</label>
                  <input value={form.application_url} onChange={(e) => setForm({ ...form, application_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email de contact</label>
                  <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone de contact</label>
                  <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={closeModal} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                  Annuler
                </button>
                <button type="button" onClick={(e) => handleSubmit(e, 'draft')} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                  Enregistrer comme brouillon
                </button>
                <button type="button" onClick={(e) => handleSubmit(e, 'published')} className="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600">
                  Publier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancingManagement;
